import { Files } from "lucide-react";
import { type ReactNode, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { TreeView, treeRowElement } from "@/components/tree/Tree";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { ItemMenu, SectionRow } from "../../projects/KnowledgeTree.tsx";
import { knowledgeTree, visibleRows } from "../../projects/knowledge-tree.ts";
import { useOpenSource } from "../../projects/SourceViewer.tsx";
import { useOpenArtefact } from "../../router.tsx";
import { ArtefactRow } from "../artefacts/ArtefactRow.tsx";
import { type ConversationScope, useArtefacts } from "../artefacts/queries.ts";
import { useAttachmentContext } from "./AttachmentControls.tsx";
import { AttachmentRow, attachmentRowActions, useAttachmentDeleteFocus } from "./AttachmentRow.tsx";
import { attachmentDownloadHref } from "./api.ts";

const SECTIONS_OPEN = ["s:attachments", "s:artefacts"];

/** Standalone working files use the established sheet and rows, with no project catalogue. */
export function ConversationFiles({ scope }: { scope: ConversationScope }) {
  const { t } = useTranslation();
  const id = useId();
  const context = useAttachmentContext();
  const own = context?.conversationId === scope.conversationId ? context : undefined;
  const list = own?.list;
  const artefacts = useArtefacts(scope);
  const { show } = useOpenArtefact();
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set(SECTIONS_OPEN));
  const [focused, setFocused] = useState<string | null>(null);
  const openSource = useOpenSource();
  const [menu, setMenu] = useState<string | null>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const container = useRef<HTMLDivElement>(null);
  const pendingArtefact = useRef<{ artefactId: string; version: number } | null>(null);
  const tree = useMemo(
    () => knowledgeTree({ attachments: list?.attachments, artefacts: artefacts.data?.artefacts }),
    [list, artefacts.data],
  );
  const rows = useMemo(
    () =>
      visibleRows(tree, expanded, null, null, ["attachments", "artefacts"]).map((row) => ({
        ...row,
        id: row.node.id,
      })),
    [tree, expanded],
  );
  // With nothing left to list, the sheet itself keeps focus.
  const deleteFocus = useAttachmentDeleteFocus(rows, container, setFocused, () =>
    container.current?.closest<HTMLElement>('[role="dialog"]'),
  );
  const toggle = (rowId: string, next: boolean) =>
    setExpanded((was) => {
      const copy = new Set(was);
      next ? copy.add(rowId) : copy.delete(rowId);
      return copy;
    });
  const chooseArtefact = (artefactId: string, version: number) => {
    pendingArtefact.current = { artefactId, version };
    setOpen(false);
  };
  return (
    <>
      <Button
        ref={trigger}
        variant="ghost"
        size="sm"
        aria-label={t("attachments.title")}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(true)}
      >
        <Files aria-hidden="true" strokeWidth={1.75} />
        <span className="hidden sm:inline">{t("attachments.title")}</span>
      </Button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="right"
          className="w-full gap-0 p-0 sm:max-w-md"
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            (event.currentTarget as HTMLElement).focus();
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            trigger.current?.focus();
            const pending = pendingArtefact.current;
            pendingArtefact.current = null;
            if (pending) {
              show(pending);
            }
          }}
        >
          <SheetHeader className="border-b border-line pr-12">
            <SheetTitle>{t("attachments.title")}</SheetTitle>
            <SheetDescription>{t("attachments.scope")}</SheetDescription>
          </SheetHeader>
          <div ref={container} className="flex min-h-0 flex-1 flex-col px-2 py-3">
            {list?.composer?.reason ? (
              <p className="px-2 pb-3 text-xs text-fg-secondary">
                {t(`attachments.${list.composer.reason}`)}
              </p>
            ) : !list?.composer && list ? (
              <p className="px-2 pb-3 text-xs text-fg-secondary">{t("attachments.readOnly")}</p>
            ) : null}
            {!list && own?.error ? (
              // A plain failure with a way out, never a placeholder that loads forever.
              <div className="flex flex-wrap items-center gap-2 px-2">
                <p role="alert" className="text-sm text-fg-secondary">
                  {t("attachments.listFailed")}
                </p>
                <Button type="button" variant="outline" size="sm" onClick={() => own.refetch?.()}>
                  {t("attachments.retry")}
                </Button>
              </div>
            ) : !list || artefacts.isPending ? (
              <Skeleton className="mx-2 h-4 w-1/2" />
            ) : rows.length === 0 ? (
              <p className="px-2 text-sm text-fg-secondary">{t("attachments.filesEmpty")}</p>
            ) : null}
            <TreeView
              label={t("attachments.title")}
              rows={rows}
              focusedId={focused}
              onFocusChange={setFocused}
              onToggle={(row, next) => toggle(row.id, next)}
              onExpandSiblings={() => setExpanded(new Set(SECTIONS_OPEN))}
              onActivate={(row, via) => {
                const node = row.node;
                if (node.kind === "section") {
                  toggle(row.id, !row.expanded);
                } else if (node.kind === "item" && node.type === "artefact") {
                  chooseArtefact(node.artefact.id, node.artefact.version);
                } else if (
                  node.kind === "item" &&
                  node.type === "attachment" &&
                  attachmentRowActions(node.attachment, list).open
                ) {
                  // A compatible file opens on a click too, like an artefact.
                  openSource(
                    {
                      kind: "attachment",
                      conversationId: scope.conversationId,
                      attachmentId: node.attachment.id,
                      title: node.attachment.title,
                    },
                    node.attachment.mediaType,
                  );
                } else if (
                  node.kind === "item" &&
                  node.type === "attachment" &&
                  via === "keyboard" &&
                  attachmentRowActions(node.attachment, list).download
                ) {
                  window.open(
                    attachmentDownloadHref(scope.conversationId, node.attachment.id),
                    "_blank",
                    "noopener,noreferrer",
                  );
                }
              }}
              onMenu={(row) => {
                if (row.node.kind === "item") {
                  setMenu(row.id);
                }
              }}
              nameOf={(row) =>
                row.node.kind === "section"
                  ? t(`knowledgePanel.section.${row.node.section}`)
                  : row.node.name
              }
              estimateSize={(row) =>
                row.node.kind === "section"
                  ? 40
                  : row.node.kind === "item" && row.node.type === "attachment"
                    ? 56
                    : 88
              }
              rowClassName={() => "group/item rounded-md hover:bg-surface-hover"}
              itemProps={(row) => ({
                ...(menu === row.id ? { "data-menu": "open" as const } : {}),
                "aria-labelledby": `${id}-${row.id}-name`,
                "aria-describedby": `${id}-${row.id}-details`,
              })}
              renderRow={(row) => {
                const node = row.node;
                const nameId = `${id}-${row.id}-name`;
                const detailsId = `${id}-${row.id}-details`;
                if (node.kind === "section") {
                  return (
                    <SectionRow
                      row={row}
                      name={t(`knowledgePanel.section.${node.section}`)}
                      labelId={nameId}
                      detailsId={detailsId}
                      restricted={null}
                      wiki={null}
                      menu={null}
                    />
                  );
                }
                if (node.kind !== "item") {
                  return null;
                }
                const rowMenu = (items: ReactNode) => (
                  <ItemMenu
                    open={menu === row.id}
                    onOpenChange={(next) => setMenu(next ? row.id : null)}
                    returnFocus={() => treeRowElement(container.current, row.id)}
                    label={t("files.actionsFor", { title: node.name })}
                  >
                    {items}
                  </ItemMenu>
                );
                if (node.type === "attachment") {
                  return (
                    <AttachmentRow
                      conversationId={scope.conversationId}
                      attachment={node.attachment}
                      list={list}
                      name={node.name}
                      nameId={nameId}
                      detailsId={detailsId}
                      style={{ paddingLeft: "34px" }}
                      menu={rowMenu}
                      returnFocus={() => treeRowElement(container.current, row.id)}
                      onDeleted={() => deleteFocus.deleted(row.id)}
                      deleteReturnFocus={() => deleteFocus.returnFocus(row.id)}
                    />
                  );
                }
                if (node.type === "artefact") {
                  return (
                    <ArtefactRow
                      artefact={node.artefact}
                      scope={scope}
                      name={node.name}
                      nameId={nameId}
                      detailsId={detailsId}
                      style={{ paddingLeft: "34px" }}
                      menu={rowMenu}
                      onOpen={() => chooseArtefact(node.artefact.id, node.artefact.version)}
                    />
                  );
                }
                return null;
              }}
            />
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
